import * as React from 'react';
import styled from 'styled-components';

import DashboardLayout from '../../layouts/DashboardLayout';
import ActionsPanel from '../../organisms/ActionsPanel';
import Loader from '../../atoms/Loader';
import ModalInfo from '../../molecules/ModalInfo';
import {
  getInspectorChoices,
  getInspectorMotdSettings,
  InspectorChoice,
  updateInspectorMotdSettings,
} from '../../api/inspectorMotd';

const displayName = (user: InspectorChoice) =>
  [user?.name, user?.email].filter(Boolean).join(' - ');

const MessageDayPage = () => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isSaving, setIsSaving] = React.useState(false);
  const [inspectors, setInspectors] = React.useState<
    InspectorChoice[]
  >([]);

  const [sender, setSender] = React.useState<number | null>(
    null,
  );
  const [sendToAll, setSendToAll] = React.useState(true);
  const [recipients, setRecipients] = React.useState<
    number[]
  >([]);

  const [error, setError] = React.useState<string>('');
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;

    Promise.all([
      getInspectorMotdSettings(),
      getInspectorChoices(),
    ])
      .then(([settingsResponse, usersResponse]) => {
        if (cancelled) return;
        const settings = settingsResponse.data;
        setSender(settings.sender ?? null);
        setSendToAll(settings.sendToAll);
        setRecipients(settings.recipients || []);
        setInspectors(usersResponse.data || []);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load Message Setup settings.');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const toggleRecipient = (id: number) => {
    setRecipients(current =>
      current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id]);
  };

  const handleSave = () => {
    if (!sendToAll && recipients.length === 0) {
      setError(
        'Select at least one inspector, or switch back to All inspectors.',
      );
      return;
    }

    setIsSaving(true);
    updateInspectorMotdSettings({
      sender,
      sendToAll,
      recipients: sendToAll ? [] : recipients,
    })
      .then(() => setSuccess(true))
      .catch(err => {
        const data = err?.response?.data;
        const message =
          data?.sender ||
          data?.recipients ||
          data?.detail ||
          'Could not save Message Setup settings.';
        setError(
          Array.isArray(message) ? message[0] : message,
        );
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <DashboardLayout>
      <Page>
        {/* Breadcrumbs live inside ActionsPanel (isBreadCrumbsVisible
            defaults to true). Kept inside Page so the 20/16px inset matches
            Jurisdiction and the other Settings pages. No search/add here, so
            only the crumbs render. */}
        <ActionsPanel isActionPanelMoreVisible={false} />
        <Card>
          <SectionTitle>
            Inspector Message Settings
          </SectionTitle>
          <Description>
            Configure who can send inspector messages via
            Message of the day (applicable only to inspectors)
            and choose whether messages are delivered to all
            inspectors or selected inspectors.
          </Description>

          {isLoading ? (
            <LoaderWrapper>
              <Loader />
            </LoaderWrapper>
          ) : (
            <>
              <Field>
                <Label htmlFor="motd-sender">
                  Who can send the message
                </Label>
                <Select
                  id="motd-sender"
                  value={
                    sender === null ? '' : String(sender)
                  }
                  onChange={event =>
                    setSender(
                      event.target.value === ''
                        ? null
                        : Number(event.target.value),
                    )
                  }
                >
                  <option value="">
                    Nobody - sending is disabled
                  </option>
                  {inspectors.map(inspector => (
                    <option
                      key={inspector.id}
                      value={inspector.id}
                    >
                      {displayName(inspector)}
                    </option>
                  ))}
                </Select>
                <Hint>
                  Only this inspector sees the send box on
                  their dashboard. Picking someone else
                  replaces them.
                </Hint>
              </Field>

              <Field>
                <Label as="span">
                  Who receives the message
                </Label>
                <RadioRow>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="motd-audience"
                      checked={sendToAll}
                      onChange={() => setSendToAll(true)}
                    />
                    All inspectors
                  </RadioLabel>
                  <RadioLabel>
                    <input
                      type="radio"
                      name="motd-audience"
                      checked={!sendToAll}
                      onChange={() => setSendToAll(false)}
                    />
                    Selected inspectors
                  </RadioLabel>
                </RadioRow>
                <Hint>
                  All inspectors reaches every inspector in
                  every branch.
                </Hint>
              </Field>

              {!sendToAll && (
                <SubCard>
                  <SubTitle>
                    Selected inspectors ({recipients.length})
                  </SubTitle>
                  {inspectors.length === 0 ? (
                    <Empty>No inspectors found.</Empty>
                  ) : (
                    <RecipientList>
                      {inspectors.map(inspector => (
                        <RecipientRow
                          key={inspector.id}
                          $selected={recipients.includes(
                            inspector.id,
                          )}
                        >
                          <input
                            type="checkbox"
                            checked={recipients.includes(
                              inspector.id,
                            )}
                            onChange={() =>
                              toggleRecipient(inspector.id)
                            }
                          />
                          {displayName(inspector)}
                        </RecipientRow>
                      ))}
                    </RecipientList>
                  )}
                </SubCard>
              )}

              <Actions>
                <PrimaryButton
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </PrimaryButton>
              </Actions>
            </>
          )}
        </Card>
      </Page>

      {(success || error.length > 0) && (
        <ModalInfo
          timeToClose={5000}
          isError={error.length > 0}
          errorMsg={error}
          onClose={() => {
            setSuccess(false);
            setError('');
          }}
        />
      )}
    </DashboardLayout>
  );
};

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

const Page = styled.div`
  padding: 20px 16px 0;
  box-sizing: border-box;
  width: 100%;
  font-family: ${FONT_STACK};

  @media only screen and (max-width: 840px) {
    padding: 12px 12px 0;
  }
`;

const Card = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  /* Matches the 20px Jurisdiction gets from its Container between the
     ActionsPanel bar and the table. */
  margin-top: 20px;
  margin-bottom: 20px;
  box-sizing: border-box;
  max-width: 720px;
`;

const SectionTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 8px;
`;

const Description = styled.p`
  margin: 0 0 24px;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const Field = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  margin-bottom: 8px;
`;

const Select = styled.select`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.875rem;
  color: var(--mainText);
  box-sizing: border-box;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: var(--neutral-400);
  }

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const Hint = styled.div`
  margin-top: 6px;
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const RadioRow = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.875rem;
  color: var(--mainText);
  cursor: pointer;
`;

const SubCard = styled.div`
  padding: 20px;
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-50);
  box-sizing: border-box;
  margin-bottom: 24px;
`;

const SubTitle = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 12px;
`;

const RecipientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
`;

const RecipientRow = styled.label<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid
    ${({ $selected }) =>
      $selected ? 'var(--primary-600)' : 'var(--border)'};
  border-radius: 8px;
  background: ${({ $selected }) =>
    $selected ? 'var(--primary-50)' : 'var(--neutral-0)'};
  font-size: 0.875rem;
  color: var(--mainText);
  cursor: pointer;
`;

const Empty = styled.div`
  text-align: center;
  padding: 32px 0;
  font-size: 0.875rem;
  color: var(--neutral-500);
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const PrimaryButton = styled.button`
  min-width: 140px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.01em;
  text-transform: none;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-300);
    cursor: not-allowed;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
`;

export default MessageDayPage;
